'use client';

import type { ClassValue } from 'clsx';

import {
  SubcategoryGrid,
  SubcategoryGridDisclosure,
  SubcategoryGridItem,
  SubcategoryGridList,
  SubcategoryGridTitle,
  SubcategoryGridToggle,
  useSubcategoryDisclosure,
  type SubcategoryGridLimits,
} from '@vs-shop/commerce-ui/subcategoryGrid';
import { Container } from '@vs-shop/ui/container';
import { Heading } from '@vs-shop/ui/heading';
import { clsxm } from '@vs-shop/utils';
import { useIntl } from 'react-intl';

import {
  SUB_CATEGORIES_DESKTOP,
  SUB_CATEGORIES_LAP,
  SUB_CATEGORIES_PHONE,
  SUB_CATEGORIES_TAB,
} from '../../../../config/category';

import CategoryCategoriesGridItem from '../../../server/page/category/CategoryCategoriesGridItem';

import type { TaxonExpandedResponse } from '@vs-shop/schemas/taxon/taxon';

/**
 * rcprofi's composition of the shared subcategory grid: the container it sits
 * in, the page's heading level, its own copy, and how many tiles each step
 * shows before the grid caps itself.
 *
 * Client-side because the cap is stateful — the disclosure hook holds it, and
 * the tiles below render in this graph as they always have.
 */

/**
 * How many tiles rcprofi shows per step before the cap. The numbers are this
 * tenant's, so they travel to the shared parts as a prop rather than living
 * beside them.
 */
const SUB_CATEGORY_LIMITS: SubcategoryGridLimits = {
  pho: SUB_CATEGORIES_PHONE,
  tab: SUB_CATEGORIES_TAB,
  lap: SUB_CATEGORIES_LAP,
  des: SUB_CATEGORIES_DESKTOP,
};

type Props = {
  taxonChildren: readonly TaxonExpandedResponse[];
  className?: ClassValue;
};

export default function CategoryCategoriesGrid({ taxonChildren, className }: Props) {
  const intl = useIntl();
  const { isExpanded, toggle, gridRef, hasHiddenTiles } = useSubcategoryDisclosure({
    count: taxonChildren.length,
    limits: SUB_CATEGORY_LIMITS,
  });

  return (
    <Container
      classNameWrapper="bg-background-intense"
      classNameInner={clsxm('flex flex-col gap-y-4 pt-8 tab:gap-y-4', className)}
    >
      <SubcategoryGridTitle render={<Heading level={2} />}>
        {intl.formatMessage({
          id: 'component.server.page.category.categoryCategoriesGrid.heading',
          defaultMessage: 'Kategorie',
        })}
      </SubcategoryGridTitle>

      <SubcategoryGrid
        ref={gridRef}
        aria-label={intl.formatMessage({
          id: 'component.server.page.category.categoryCategoriesGrid.subcategoriesLabel',
          defaultMessage: 'Podkategorie',
        })}
      >
        <SubcategoryGridList>
          {taxonChildren.map((taxon, index) => (
            <SubcategoryGridItem
              key={taxon.code}
              index={index}
              limits={SUB_CATEGORY_LIMITS}
              expanded={isExpanded}
            >
              <CategoryCategoriesGridItem taxon={taxon} />
            </SubcategoryGridItem>
          ))}
        </SubcategoryGridList>
      </SubcategoryGrid>

      {hasHiddenTiles && (
        <SubcategoryGridDisclosure count={taxonChildren.length} limits={SUB_CATEGORY_LIMITS}>
          <SubcategoryGridToggle aria-expanded={isExpanded} onClick={toggle}>
            {isExpanded
              ? intl.formatMessage({
                  id: 'component.server.page.category.categoryCategoriesGrid.showLess',
                  defaultMessage: 'Zobrazit méně kategorií',
                })
              : intl.formatMessage({
                  id: 'component.server.page.category.categoryCategoriesGrid.showAll',
                  defaultMessage: 'Zobrazit všechny kategorie',
                })}
          </SubcategoryGridToggle>
        </SubcategoryGridDisclosure>
      )}
    </Container>
  );
}
